import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import WorkspaceBanners from "@/components/common/WorkspaceBanners";
import { EmptyState } from "@/components/common/States";
import MediaFrame from "@/components/common/MediaFrame";
import JobDetailModal from "@/components/common/JobDetailModal";
import AssetDetailModal from "@/components/common/AssetDetailModal";
import { useStudio, useLookups } from "@/store/StudioContext";
import { groupItemsByDate } from "@/lib/dateUtils";
import { t } from "@/i18n";

import ImageActionBar, { downloadAsset } from "@/components/common/ImageActionBar";
import ImageCardMenu from "@/components/common/ImageCardMenu";
import DeleteAssetDialog from "@/components/common/DeleteAssetDialog";
import { Trash2 } from "lucide-react";

/**
 * Checkbox con soporte de estado parcial (indeterminate) para grupos de fecha.
 */
const DateGroupCheckbox = ({ isChecked, isIndeterminate, onChange, testId, ariaLabel }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(isIndeterminate);
    }
  }, [isIndeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      data-testid={testId}
      aria-label={ariaLabel}
      checked={isChecked}
      onChange={onChange}
      className="h-4 w-4 rounded border-[#555] bg-[#1a1a1a] text-[#f5b544] focus:ring-[#f5b544] focus:ring-offset-0 cursor-pointer accent-[#f5b544]"
    />
  );
};

const GalleryPage = () => {
  const { state, dispatch } = useStudio();
  const { projectById, sceneById, assetById } = useLookups();
  const navigate = useNavigate();
  const [type, setType] = useState("all");
  const [projectId, setProjectId] = useState("all");
  const [openAsset, setOpenAsset] = useState(null);
  const [viewJobDetail, setViewJobDetail] = useState(null);

  const lang = state.onboarding?.language || "es";

  const getAssetPrompt = (asset) => {
    if (!asset) return "";
    if (asset.prompt) return asset.prompt;
    if (asset.meta?.prompt) return asset.meta.prompt;
    if (asset.params?.prompt) return asset.params.prompt;
    if (asset.sceneId && sceneById[asset.sceneId]?.prompt) return sceneById[asset.sceneId].prompt;
    const job = state.jobs.find(
      (j) => j.resultAssetId === asset.id || j.resultAssetIds?.includes(asset.id)
    );
    if (job?.prompt) return job.prompt;
    return "";
  };

  // ── Selección múltiple ──────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [deleteSceneId, setDeleteSceneId] = useState(null);

  const list = useMemo(() => {
    return state.assets
      .filter(
        (a) =>
          (type === "all" || a.type === type) &&
          (projectId === "all" || a.projectId === projectId || (projectId === "none" && !a.projectId))
      )
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [state.assets, type, projectId]);

  // Si cambia el filtro (tipo o proyecto), eliminar selecciones ocultas que no estén en la vista activa
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(list.map((a) => a.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      if (next.size === 0) {
        setSelectMode(false);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [list]);

  // Agrupamiento cronológico estilo Freepik por día (descendente)
  const groupedAssets = useMemo(() => {
    return groupItemsByDate(list, (a) => a.createdAt, lang);
  }, [list, lang]);

  const isSelecting = selectMode || selected.size > 0;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      if (next.size === 0) setSelectMode(false);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(list.map((a) => a.id)));
    setSelectMode(true);
  };

  const deselectAll = () => {
    setSelected(new Set());
    setSelectMode(false);
  };

  const cancelSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  // Toggle de todo un grupo de fecha dentro del filtro activo
  const toggleDateGroup = (group) => {
    const groupIds = group.items.map((a) => a.id);
    const allSelected = groupIds.length > 0 && groupIds.every((id) => selected.has(id));

    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        groupIds.forEach((id) => next.delete(id));
      } else {
        groupIds.forEach((id) => next.add(id));
      }
      if (next.size === 0) {
        setSelectMode(false);
      }
      return next;
    });
    if (!allSelected) {
      setSelectMode(true);
    }
  };

  // Iniciar eliminación de una fecha específica
  const askDeleteDateGroup = (group) => {
    const groupSelectedIds = group.items.map((a) => a.id).filter((id) => selected.has(id));
    if (groupSelectedIds.length === 0) return;
    setPendingDeleteIds(groupSelectedIds);
    setDeleteSceneId(null);
    setConfirmDelete(true);
  };

  // Iniciar eliminación de todas las seleccionadas
  const askDeleteAllSelected = () => {
    if (selected.size === 0) return;
    setPendingDeleteIds([...selected]);
    setDeleteSceneId(null);
    setConfirmDelete(true);
  };

  const askDelete = (ids, sceneId = null) => {
    setPendingDeleteIds(ids);
    setDeleteSceneId(sceneId);
    setConfirmDelete(true);
  };

  const closeDelete = () => {
    setConfirmDelete(false);
    setDeleteSceneId(null);
    setPendingDeleteIds([]);
  };

  const handleDoneDelete = (result) => {
    const deletedIds = Array.isArray(result?.deleted) ? result.deleted : [];
    if (deletedIds.length > 0) {
      setSelected((prev) => {
        const next = new Set(prev);
        deletedIds.forEach((id) => next.delete(id));
        if (next.size === 0) setSelectMode(false);
        return next;
      });
    }
    setOpenAsset(null);
    setPendingDeleteIds([]);
  };

  const openInEditor = (a) => {
    if (!a.projectId) return;
    dispatch({
      type: "SELECT",
      payload: { projectId: a.projectId, patch: { assetId: a.id, sceneId: a.sceneId || null } },
    });
    navigate(`/projects/${a.projectId}/editor`);
  };
  const sendToReference = (a) => navigate("/characters", { state: { prefillMasterAssetId: a.id } });
  const openUpscale = (a) => {
    if (a.type !== "image") return;
    navigate(`/tools/upscale?asset=${a.id}${a.projectId ? `&project=${a.projectId}` : ""}`);
  };

  return (
    <Page testId="gallery-page">
      <div className="pb-28">
        <PageHeader title={t.gallery.title} subtitle={t.gallery.subtitle} />
        <WorkspaceBanners />

        {/* ── Filtros siempre disponibles ── */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {["all", "image", "video"].map((k) => (
            <button
              key={k}
              data-testid={`gallery-filter-${k}`}
              onClick={() => setType(k)}
              className={`v-btn-ghost v-btn-sm ${type === k ? "border-[#f5b544] text-[#f5b544]" : ""}`}
            >
              {t.gallery.filters[k]}
            </button>
          ))}
          <span className="mx-2 hidden h-5 w-px bg-[#303030] sm:block" />
          <label className="flex items-center gap-1.5 text-xs text-[#a3a3a3]">
            <span>{t.gallery.projectFilterLabel}:</span>
            <select
              data-testid="gallery-filter-project"
              className="v-input h-8 w-auto text-xs"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="all">{t.common.all}</option>
              <option value="none">{t.common.noProject}</option>
              {state.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <span className="ml-auto" />
          {list.length > 0 && !isSelecting && (
            <button
              data-testid="gallery-select-mode"
              onClick={() => setSelectMode(true)}
              className="v-btn-ghost v-btn-sm"
            >
              {t.gallery.selectMode || "Seleccionar"}
            </button>
          )}
        </div>

        {/* ── Acciones superiores de selección (arriba del listado) ── */}
        {isSelecting && (
          <div
            data-testid="gallery-top-actions"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#303030] bg-[#161616] p-3 text-xs md:text-sm"
          >
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="font-medium text-[#f4f1ec]" data-testid="gallery-select-count">
                {selected.size} seleccionada{selected.size !== 1 ? "s" : ""}
              </span>
              <button
                data-testid="gallery-deselect-all"
                onClick={deselectAll}
                className="v-btn-ghost v-btn-sm text-xs"
              >
                {t.gallery.deselect || "Deseleccionar"}
              </button>
              <button
                data-testid="gallery-select-all"
                onClick={selectAll}
                className="v-btn-ghost v-btn-sm text-xs"
              >
                {t.gallery.selectAll || "Seleccionar todas"}
              </button>
              <button
                data-testid="gallery-select-cancel"
                onClick={cancelSelect}
                className="v-btn-ghost v-btn-sm text-xs text-[#888]"
              >
                {t.common.cancel || "Cancelar"}
              </button>
            </div>
            <div>
              <button
                data-testid="gallery-delete-selected"
                onClick={askDeleteAllSelected}
                disabled={selected.size === 0}
                className="v-btn-ghost v-btn-sm border-red-500/40 text-red-400 hover:border-red-500 hover:text-red-300 disabled:opacity-40"
              >
                {t.gallery.deleteSelected
                  ? t.gallery.deleteSelected(selected.size)
                  : `Eliminar seleccionadas (${selected.size})`}
              </button>
            </div>
          </div>
        )}

        {/* ── Grid de assets agrupados cronológicamente estilo Freepik ── */}
        {list.length === 0 ? (
          <EmptyState title={t.gallery.empty} body={t.gallery.emptyBody} testId="gallery-empty" />
        ) : (
          <div className="space-y-8">
            {groupedAssets.map((group) => {
              const groupIds = group.items.map((a) => a.id);
              const groupSelectedCount = groupIds.filter((id) => selected.has(id)).length;
              const isAllGroupSelected = groupIds.length > 0 && groupSelectedCount === groupIds.length;
              const isPartialGroupSelected = groupSelectedCount > 0 && !isAllGroupSelected;

              return (
                <div key={group.key} data-testid={`gallery-group-${group.key}`} className="space-y-3">
                  {/* Encabezado del grupo por fecha */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#262626] pb-2">
                    <div className="flex items-center gap-2.5">
                      <DateGroupCheckbox
                        isChecked={isAllGroupSelected}
                        isIndeterminate={isPartialGroupSelected}
                        onChange={() => toggleDateGroup(group)}
                        testId={`gallery-group-checkbox-${group.key}`}
                        ariaLabel={`Seleccionar grupo de fecha ${group.title}`}
                      />
                      <span className="h-2 w-2 rounded-full bg-[#f5b544]" />
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#ededed]">
                        {group.title}
                      </h3>
                      <span className="text-[11px] font-mono text-[#8a8a8a] bg-[#1a1a1a] px-2 py-0.5 rounded border border-[#2a2a2a]">
                        {group.items.length} {group.items.length === 1 ? "recurso" : "recursos"}
                      </span>
                      {groupSelectedCount > 0 && (
                        <span
                          data-testid={`gallery-group-count-${group.key}`}
                          className="text-[11px] font-mono text-[#f5b544] bg-[#f5b544]/10 px-2 py-0.5 rounded border border-[#f5b544]/30"
                        >
                          {groupSelectedCount} seleccionada{groupSelectedCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>

                    {groupSelectedCount > 0 && (
                      <button
                        data-testid={`gallery-group-delete-${group.key}`}
                        onClick={() => askDeleteDateGroup(group)}
                        className="v-btn-ghost v-btn-sm border-red-500/40 text-red-400 hover:border-red-500 hover:text-red-300 text-xs py-1"
                      >
                        {t.gallery.deleteSelectedDate
                          ? t.gallery.deleteSelectedDate(groupSelectedCount)
                          : `Eliminar seleccionadas de esta fecha (${groupSelectedCount})`}
                      </button>
                    )}
                  </div>

                  {/* Grid de recursos del día */}
                  <div className="columns-2 gap-4 md:columns-3 xl:columns-4 [&>*]:mb-4 [&>*]:break-inside-avoid">
                    {group.items.map((a) => {
                      const isSelected = selected.has(a.id);
                      return (
                        <div
                          key={a.id}
                          data-testid={`gallery-item-${a.id}`}
                          className={`group block w-full text-left relative ${isSelecting ? "cursor-pointer" : ""}`}
                        >
                          {/* Overlay / Checkbox de selección individual */}
                          {isSelecting && (
                            <button
                              type="button"
                              data-testid={`gallery-select-item-${a.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelect(a.id);
                              }}
                              className={`absolute top-2 right-2 z-10 flex h-5 w-5 items-center justify-center rounded border-2 text-[11px] font-bold transition-all shadow-md ${
                                isSelected
                                  ? "border-[#f5b544] bg-[#f5b544] text-black"
                                  : "border-[#888] bg-[#141414]/90 text-transparent hover:border-[#f5b544]"
                              }`}
                            >
                              ✓
                            </button>
                          )}
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => (isSelecting ? toggleSelect(a.id) : setOpenAsset(a))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                isSelecting ? toggleSelect(a.id) : setOpenAsset(a);
                              }
                            }}
                            className="relative overflow-hidden rounded-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5b544]"
                          >
                            <MediaFrame
                              asset={a}
                              className={`w-full transition-[border-color] group-hover:border-[#4a4a4a] ${
                                isSelecting && isSelected
                                  ? "ring-2 ring-[#f5b544] ring-offset-1 ring-offset-[#111]"
                                  : ""
                              }`}
                              fit="contain"
                            />
                            {!isSelecting && a.type === "image" && (
                              <>
                                <ImageCardMenu asset={a} prompt={getAssetPrompt(a)} />
                                <ImageActionBar
                                  asset={a}
                                  onDownload={() => downloadAsset(a)}
                                  onDelete={() => askDelete([a.id])}
                                  onEdit={a.projectId ? () => openInEditor(a) : undefined}
                                />
                              </>
                            )}
                            {!isSelecting && a.type === "video" && (
                              <button
                                type="button"
                                data-testid={`gallery-video-delete-${a.id}`}
                                title="Eliminar"
                                aria-label="Eliminar"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  askDelete([a.id]);
                                }}
                                className="absolute right-2 top-2 z-10 rounded bg-[#101010]/85 p-1.5 text-[#d8d4cd] opacity-80 transition-opacity hover:bg-red-500/30 hover:text-red-300 group-hover:opacity-100 focus-visible:opacity-100"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-2 px-0.5">
                            <span className="truncate text-xs text-[#d8d4cd]">{a.label || t.common.untitled}</span>
                            <span className="shrink-0 text-[10px] text-[#6b6b6b]">
                              {a.width && a.height ? `${a.width}×${a.height}` : ""}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Controles al final del listado ── */}
        {selected.size > 0 && list.length > 0 && (
          <div
            data-testid="gallery-bottom-actions"
            className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#303030] bg-[#161616] p-4"
          >
            <div className="text-sm font-medium text-[#f4f1ec]">
              {selected.size} seleccionada{selected.size !== 1 ? "s" : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="gallery-bottom-deselect"
                onClick={deselectAll}
                className="v-btn-ghost v-btn-sm"
              >
                {t.gallery.deselect || "Deseleccionar"}
              </button>
              <button
                data-testid="gallery-bottom-delete"
                onClick={askDeleteAllSelected}
                className="v-btn-ghost v-btn-sm border-red-500/40 text-red-400 hover:border-red-500 hover:text-red-300"
              >
                {t.gallery.deleteSelected
                  ? t.gallery.deleteSelected(selected.size)
                  : `Eliminar seleccionadas (${selected.size})`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Barra fija flotante mientras haya selección ── */}
      {selected.size > 0 && (
        <div
          data-testid="gallery-floating-bar"
          className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-xl rounded-xl border border-[#3a3a3a] bg-[#161616]/95 px-4 py-3 shadow-2xl backdrop-blur-md flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#f5b544]" />
            <span className="text-xs md:text-sm font-medium text-[#f4f1ec]" data-testid="gallery-floating-count">
              {selected.size} seleccionada{selected.size !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="gallery-floating-deselect"
              onClick={deselectAll}
              className="v-btn-ghost v-btn-sm text-xs"
            >
              {t.gallery.deselect || "Deseleccionar"}
            </button>
            <button
              data-testid="gallery-floating-delete"
              onClick={askDeleteAllSelected}
              className="v-btn-sm rounded border border-red-500/60 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/20"
            >
              {t.gallery.deleteSelected
                ? t.gallery.deleteSelected(selected.size)
                : `Eliminar seleccionadas (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {/* ── Diálogo de confirmación y eliminación con protección de medios ── */}
      <DeleteAssetDialog
        open={confirmDelete}
        assetIds={pendingDeleteIds}
        sceneId={deleteSceneId}
        onClose={closeDelete}
        onDone={handleDoneDelete}
      />

      {/* ── Modal de detalle de asset ── */}
      <AssetDetailModal
        asset={openAsset}
        open={Boolean(openAsset) && !isSelecting}
        onClose={() => setOpenAsset(null)}
        projectId={openAsset?.projectId}
        onUseAsReference={(a) => sendToReference(a)}
        onOpenInEditor={(a) => openInEditor(a)}
        onOpenUpscale={(a) => openUpscale(a)}
        onDelete={(a) => askDelete([a.id], a.sceneId || null)}
        testId="gallery-detail"
      />

      {/* ── Modal de detalles de generación ── */}
      <JobDetailModal
        job={viewJobDetail}
        open={Boolean(viewJobDetail)}
        onOpenChange={(isOpen) => !isOpen && setViewJobDetail(null)}
        assetById={assetById}
        projectById={projectById}
        sceneById={sceneById}
        lang={lang}
      />
    </Page>
  );
};

export default GalleryPage;
